import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { initPdf, drawHeader, drawFooter, drawPageNumbers, safeFilename, PAGE } from './pdfTheme';
import type { Competition, Category } from '../../types/domain';
import type { DvojbojRanking } from '../../types/dvojboj';
import type { AgeSubcategory } from '../../types/individualEntry';

interface Args {
  competition: Competition;
  category: Category;
  rankings: DvojbojRanking[];
  /** Nazvy 2 disciplín dvojboje (v poradi sort_order). */
  disciplineNames: string[];
}

const AGE_LABELS: Partial<Record<AgeSubcategory, string>> = {
  MLADSI_DOROSTENEC: 'Mladší dorostenci',
  STREDNI_DOROSTENEC: 'Střední dorostenci',
  STARSI_DOROSTENEC: 'Starší dorostenci',
  MLADSI_DOROSTENKA: 'Mladší dorostenky',
  STREDNI_DOROSTENKA: 'Střední dorostenky',
  STARSI_DOROSTENKA: 'Starší dorostenky',
  OSTATNI: 'Ostatní',
};

/**
 * TYP B — Dvojboj jednotlivci (součet pořadí 2 disciplín). Jedna stránka per věková subkategorie.
 */
export function generateDvojbojRanking({ competition, category, rankings, disciplineNames }: Args): void {
  const doc = initPdf('portrait');
  const dateLoc = `${dayjs(competition.competitionDate).format('D. M. YYYY')}${competition.location ? ', ' + competition.location : ''}`;

  if (rankings.length === 0) {
    drawHeader(doc, {
      title: 'Dvojboj jednotlivci',
      competitionName: competition.name,
      competitionDateLocation: dateLoc,
      categoryLabel: category.name,
    });
    doc.text('Žádní hodnocení závodníci.', 105, 60, { align: 'center' });
    drawFooter(doc, {
      headJudgeName: competition.headJudgeName,
      eventLeaderName: competition.eventLeaderName,
      commissarName: competition.commissarName,
    });
    drawPageNumbers(doc);
    doc.save(safeFilename(`${competition.shortCode}_${category.name}_dvojboj.pdf`));
    return;
  }

  rankings.forEach((group, idx) => {
    if (idx > 0) doc.addPage();
    const headerEnd = drawHeader(doc, {
      title: 'Dvojboj jednotlivci',
      competitionName: competition.name,
      competitionDateLocation: dateLoc,
      categoryLabel: `${category.name} — ${AGE_LABELS[group.ageSubcategory] ?? group.ageSubcategory}`,
    });

    const head = [[
      'Pořadí',
      'Závodník',
      'SDH',
      ...disciplineNames.map((d) => `${d}\n(P.)`),
      'Součet bodů',
    ]];
    const body = group.items.map((it) => [
      it.overallRank != null ? String(it.overallRank) : '—',
      `${it.firstName} ${it.lastName}`,
      it.sdhUnitName ?? '',
      ...it.ranksInDiscs.map((r) => String(r)),
      String(it.sumPoints),
    ]);

    autoTable(doc, {
      startY: headerEnd,
      margin: { left: PAGE.margin, right: PAGE.margin, bottom: 35 },
      head,
      body,
      styles: { font: 'DejaVu', fontSize: 9, cellPadding: 1.5, lineColor: [220, 220, 220] },
      headStyles: { fillColor: [198, 40, 40], textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { halign: 'center', cellWidth: 18, fontStyle: 'bold' },
        [3 + disciplineNames.length]: { halign: 'center', fontStyle: 'bold', cellWidth: 22 },
      },
      didDrawPage: () =>
        drawFooter(doc, {
          headJudgeName: competition.headJudgeName,
          eventLeaderName: competition.eventLeaderName,
          commissarName: competition.commissarName,
        }),
    });
  });

  drawPageNumbers(doc);

  doc.save(safeFilename(`${competition.shortCode}_${category.name}_dvojboj.pdf`));
}
